// Level Format Converter
// Converts between GridLevel (editor tile format) and PlatformerLevel (game engine format)

import { Platform, PlatformerLevel } from './CharacterTypes';
import { GRID_CONSTANTS } from './constants/GridConstants';
import {
  GridLevel,
  LayerType,
  TileType,
  decodeTileKey,
} from './TileEditorTypes';

export class LevelFormatConverter {
  // Use actual TileType enum values for consistency
  private static readonly TILE_TYPE_FLOOR = TileType.FLOOR;
  private static readonly TILE_TYPE_STONE = TileType.STONE;
  private static readonly TILE_TYPE_WOOD = TileType.WOOD;
  private static readonly TILE_TYPE_WATER = TileType.WATER;
  private static readonly TILE_TYPE_LAVA = TileType.LAVA;
  private static readonly TILE_TYPE_ICE = TileType.ICE;
  private static readonly TILE_TYPE_WALL = TileType.WALL;
  private static readonly TILE_TYPE_BOUNCY = TileType.HALF_WALL;
  private static readonly TILE_TYPE_MOVING = TileType.DOOR;
  private static readonly TILE_TYPE_FALLING = TileType.TRAP;
  private static readonly TILE_TYPE_TELEPORTER = TileType.TELEPORTER;
  private static readonly TILE_TYPE_ROPE = TileType.ROPE;

  /**
   * Convert a GridLevel (tile-based) to a PlatformerLevel (platform-based)
   * SPARSE STORAGE: Iterates only placed tiles instead of full grid
   */
  public static gridToPlatformer(gridLevel: GridLevel): PlatformerLevel {
    console.log(`[LevelFormatConverter] Converting GridLevel to PlatformerLevel: ${gridLevel.id}`);

    const platforms: Platform[] = [];

    // Find the ground/platform layer
    const groundLayer = gridLevel.layers.find(layer => layer.name === LayerType.GROUND);
    if (!groundLayer || !groundLayer.tiles || groundLayer.tiles.size === 0) {
      console.warn(`[LevelFormatConverter] No ground layer tiles found in ${gridLevel.id}, creating empty level`);
      return this.createEmptyLevel(gridLevel);
    }

    // Get all tile positions and sort by Y then X for contiguous detection
    const tileEntries = Array.from(groundLayer.tiles.entries())
      .map(([key, tileType]) => {
        const { x, y } = decodeTileKey(key);
        return { x, y, tileType, key };
      })
      .sort((a, b) => a.y - b.y || a.x - b.x);

    // Group tiles by row for contiguous platform detection
    const rows = new Map<number, { x: number; tileType: number }[]>();
    for (const entry of tileEntries) {
      if (!rows.has(entry.y)) {
        rows.set(entry.y, []);
      }
      rows.get(entry.y)!.push({ x: entry.x, tileType: entry.tileType });
    }

    // Convert contiguous tiles in each row to platforms
    for (const [y, rowTiles] of rows) {
      // Sort by X within row
      rowTiles.sort((a, b) => a.x - b.x);

      let i = 0;
      while (i < rowTiles.length) {
        const startTile = rowTiles[i];
        const tileType = startTile.tileType;
        let startX = startTile.x;
        let width = 1;

        // Find contiguous same-type tiles
        while (i + 1 < rowTiles.length &&
               rowTiles[i + 1].x === startX + width &&
               rowTiles[i + 1].tileType === tileType) {
          width++;
          i++;
        }

        // Create platform
        const platform: Platform = {
          id: `plat_${y}_${startX}_${Date.now()}`,
          x: startX * (gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE),
          y: y * (gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE),
          width: width * (gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE),
          height: (gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE),
          type: this.tileTypeToPlatformType(tileType),
          color: this.tileTypeToColor(tileType),
          isOneWay: false
        };

        // Apply surface overlay
        const tileSurface = gridLevel.surfaces?.find(s => s.x === startX && s.y === y);
        if (tileSurface) {
          platform.hasSurface = true;
          platform.surfaceType = tileSurface.surfaceType;
          platform.surfaceHeight = Math.floor((gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE) * 0.25);
        }

        platforms.push(platform);
        i++;
      }
    }

    // Also scan entities layer for special platforms and objects
    const entitiesLayer = gridLevel.layers.find(layer => layer.name === LayerType.ENTITIES);
    const levelEntities: any[] = []; // Use any to bypass import issues for now, or import LevelEntity

    if (entitiesLayer?.entities) {
      entitiesLayer.entities.forEach((entity, index) => {
        // Determine if this is a platform-like entity or an actor/object
        const type = entity.type || 'normal';
        const isPlatformType = ['normal', 'moving', 'falling', 'bouncy', 'ice', 'teleporter', 'rope', 'cover'].includes(type);

        if (isPlatformType) {
          const platform: Platform = {
            id: entity.id || `ent_${entity.x}_${entity.y}_${index}`,
            x: entity.x * (gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE),
            y: entity.y * (gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE),
            width: (entity.width || 1) * (gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE),
            height: (entity.height || 1) * (gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE),
            type: type as Platform['type'],
            color: entity.color || '#FFD700',
            isOneWay: entity.isOneWay || false,
            velocity: entity.velocity as any,
            teleporterConfig: entity.properties?.teleporterConfig
          } as any;
          // Preserve the full properties object for TeleporterManager to read editor links
          if (entity.properties) {
            (platform as any).properties = entity.properties;
            platform.attributes = {};
            for (const key in entity.properties) {
              if (typeof entity.properties[key] === 'string') {
                platform.attributes[key] = entity.properties[key];
              }
            }
          }
          platforms.push(platform);
          console.log(`[LevelFormatConverter] Created entity platform: ${platform.id} (${type})`);
        } else {
          // It's a game entity (NPC, Mob, Chest, etc.)
          levelEntities.push({
            id: entity.id || `actor_${entity.x}_${entity.y}_${index}`,
            type: type,
            x: entity.x * (gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE),
            y: entity.y * (gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE),
            width: (entity.width || 1) * (gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE),
            height: (entity.height || 1) * (gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE),
            properties: entity.properties
          });
          console.log(`[LevelFormatConverter] Created LevelEntity: ${type} at ${entity.x},${entity.y}`);
        }
      });
    }

    // Create the PlatformerLevel
    const platformerLevel: PlatformerLevel = {
      id: gridLevel.id,
      name: gridLevel.name,
      // Preserve metadata (lobbyTools, author, etc.) across conversion.
      metadata: gridLevel.metadata,
      width: gridLevel.widthTiles * (gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE),
      height: gridLevel.heightTiles * (gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE),
      platforms,
      spawnPoints: gridLevel.spawnPoints?.length > 0
        ? gridLevel.spawnPoints.map(p => ({
            x: p.x * (gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE),
            y: p.y * (gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE)
          }))
        : [{ x: 100, y: 100 }],
      gravity: gridLevel.gravity || 1500,
      entities: levelEntities,
      backgroundColor: gridLevel.backgroundColor || '#87CEEB',
      // Per-level atmosphere (Scene FX v3 M3) — carried verbatim (opaque to shared).
      effectLayers: gridLevel.effectLayers,
      // Per-level scene metadata (scene-schema) — carried verbatim (opaque to shared).
      scene: gridLevel.scene,
      // Preserve the original GridLevel for lossless editing (including composites)
      editorGrid: gridLevel
    };

    console.log(`[LevelFormatConverter] Conversion complete: ${platforms.length} platforms created, ${levelEntities.length} entities`);
    console.log(`[LevelFormatConverter] Level dimensions: ${platformerLevel.width} x ${platformerLevel.height}`);
    if (gridLevel.composites?.length) {
      console.log(`[LevelFormatConverter] Preserved ${gridLevel.composites.length} composite tiles`);
    }
    if (levelEntities.length > 0) {
      console.log(`[LevelFormatConverter] Entities:`, levelEntities.map(e => `${e.type} at (${e.x},${e.y})`).join(', '));
    }

    return platformerLevel;
  }

  private static tileTypeToPlatformType(tileType: number): Platform['type'] {
    switch (tileType) {
    case this.TILE_TYPE_BOUNCY: return 'bouncy';
    case this.TILE_TYPE_MOVING: return 'moving';
    case this.TILE_TYPE_FALLING: return 'falling';
    case this.TILE_TYPE_ICE: return 'ice';
    case this.TILE_TYPE_TELEPORTER: return 'teleporter';
    case this.TILE_TYPE_ROPE: return 'rope';
      // LAVA and WATER are 'normal' platforms with hazard attributes
    default: return 'normal';
    }
  }

  /**
   * Tile-render verification: returns the color for a tile type, or null
   * if the tile type has no explicit color mapping (would fall through to
   * the default grey #CCCCCC). Used by the tile verification test to catch
   * missing cases at build time.
   */
  public static getTileColor(tileType: number): string | null {
    const result = this.tileTypeToColor(tileType);
    return result === '#CCCCCC' ? null : result;
  }

  /**
   * Tile-render verification: returns the platform type for a tile type.
   * Used by the tile verification test to validate platform type mappings.
   */
  public static getTilePlatformType(tileType: number): Platform['type'] {
    return this.tileTypeToPlatformType(tileType);
  }

  private static tileTypeToColor(tileType: number): string {
    switch (tileType) {
    // Ground types
    case this.TILE_TYPE_FLOOR: return '#8B7355';   // Brown floor
    case TileType.GRASS: return '#7CFC00';        // Green grass
    case this.TILE_TYPE_STONE: return '#808080';   // Gray stone
    case this.TILE_TYPE_WOOD: return '#D2691E';    // Wood
    case this.TILE_TYPE_WATER: return '#1E90FF';   // Blue water
    case this.TILE_TYPE_LAVA: return '#FF4500';    // Orange-red lava
    case this.TILE_TYPE_ICE: return '#E0FFFF';     // Cyan ice

      // Collision types
    case this.TILE_TYPE_WALL: return '#4A4A4A';    // Dark gray wall
    case TileType.DOOR: return '#8B4513';          // Brown door
    case TileType.WINDOW: return '#87CEEB';        // Light blue window
    case TileType.HALF_WALL: return '#696969';     // Dim gray half wall

      // Special platform types
    case this.TILE_TYPE_BOUNCY: return '#FF69B4';  // Pink bouncy
    case this.TILE_TYPE_MOVING: return '#00BFFF';  // Blue moving
    case this.TILE_TYPE_FALLING: return '#A0522D'; // Sienna falling (distinct from DOOR brown)
    case this.TILE_TYPE_TELEPORTER: return '#9370DB'; // Purple teleporter
    case this.TILE_TYPE_ROPE: return '#D2B48C';    // Tan/beige rope

      // Trigger / zone types
    case TileType.SAFE_ZONE: return '#00CED1';     // Dark cyan safe zone
    case TileType.EXIT_ZONE: return '#FF1493';     // Deep pink exit zone

    default: return '#CCCCCC'; // Default gray
    }
  }

  private static createEmptyLevel(gridLevel: GridLevel): PlatformerLevel {
    console.warn(`[LevelFormatConverter] Creating empty fallback level for ${gridLevel.id}`);

    return {
      id: gridLevel.id,
      name: gridLevel.name,
      // Preserve metadata (lobbyTools, author, etc.) across conversion.
      metadata: gridLevel.metadata,
      width: gridLevel.widthTiles * (gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE),
      height: gridLevel.heightTiles * (gridLevel.tileSize || GRID_CONSTANTS.TILE_SIZE),
      platforms: [],
      spawnPoints: [{ x: 100, y: 100 }],
      gravity: gridLevel.gravity || 1500,
      effectLayers: gridLevel.effectLayers,
      scene: gridLevel.scene,
      backgroundColor: gridLevel.backgroundColor || '#87CEEB'
    };
  }

  /**
   * Convert a PlatformerLevel (platform-based) to a GridLevel (tile-based)
   * SPARSE STORAGE: Uses Map instead of dense 2D arrays
   */
  public static platformerToGrid(level: PlatformerLevel): GridLevel {
    const tileSize = GRID_CONSTANTS.TILE_SIZE;

    // SPARSE: Start with 0,0 bounds, expand as tiles are placed
    const groundTiles = new Map<string, number>();
    const collisionTiles = new Map<string, number>();
    const objectTiles = new Map<string, number>();
    const triggerTiles = new Map<string, number>();

    console.log(`[LevelFormatConverter] Reverse converting PlatformerLevel → GridLevel: ${level.id}`);

    // Build layers with sparse storage
    const layers = [
      { name: LayerType.GROUND, visible: true, locked: false, opacity: 1.0, tiles: groundTiles },
      { name: LayerType.COLLISION, visible: true, locked: false, opacity: 1.0, tiles: collisionTiles },
      { name: LayerType.OBJECTS, visible: true, locked: false, opacity: 1.0, tiles: objectTiles },
      { name: LayerType.TRIGGERS, visible: true, locked: false, opacity: 1.0, tiles: triggerTiles },
      { name: LayerType.ENTITIES, visible: true, locked: false, opacity: 1.0, tiles: new Map<string, number>(), entities: [] as any[] },
    ];

    const groundLayer = layers[0];
    let minX = 0, maxX = 0, minY = 0, maxY = 0;
    let filledTiles = 0;

    // Rasterize each platform into tile positions on the GROUND layer
    for (const platform of level.platforms) {
      const tileType = this.platformTypeToPlatformTileType(platform.type);
      if (tileType === 0) continue;

      const startTX = Math.floor(platform.x / tileSize);
      const startTY = Math.floor(platform.y / tileSize);
      const endTX = Math.ceil((platform.x + platform.width) / tileSize);
      const endTY = Math.ceil((platform.y + platform.height) / tileSize);

      // Track bounds
      minX = Math.min(minX, startTX);
      maxX = Math.max(maxX, endTX);
      minY = Math.min(minY, startTY);
      maxY = Math.max(maxY, endTY);

      for (let ty = startTY; ty < endTY; ty++) {
        for (let tx = startTX; tx < endTX; tx++) {
          groundLayer.tiles.set(`${tx},${ty}`, tileType);
          filledTiles++;
        }
      }
    }

    // Convert entities
    const entitiesLayer = layers[4];
    if (level.entities) {
      entitiesLayer.entities = level.entities.map((entity, index) => {
        const tx = Math.floor(entity.x / tileSize);
        const ty = Math.floor(entity.y / tileSize);

        // Track entity positions in bounds
        minX = Math.min(minX, tx);
        maxX = Math.max(maxX, tx + 1);
        minY = Math.min(minY, ty);
        maxY = Math.max(maxY, ty + 1);

        return {
          id: entity.id || `entity_${index}`,
          type: entity.type,
          x: tx,
          y: ty,
          width: entity.width ? Math.ceil(entity.width / tileSize) : 1,
          height: entity.height ? Math.ceil(entity.height / tileSize) : 1,
          properties: entity.properties || {},
        };
      });
    }

    // Convert spawn points from world coords to tile coords
    const spawnPoints = (level.spawnPoints || []).map(sp => {
      const tx = Math.floor(sp.x / tileSize);
      const ty = Math.floor(sp.y / tileSize);

      minX = Math.min(minX, tx);
      maxX = Math.max(maxX, tx + 1);
      minY = Math.min(minY, ty);
      maxY = Math.max(maxY, ty + 1);

      return { x: tx, y: ty };
    });

    // SPARSE: No artificial bounds - use actual bounds or defaults if empty
    const gridWidth = Math.max(GRID_CONSTANTS.DEFAULT_WIDTH, maxX - minX);
    const gridHeight = Math.max(GRID_CONSTANTS.DEFAULT_HEIGHT, maxY - minY);

    const gridLevel: GridLevel = {
      id: level.id,
      name: level.name,
      version: '1.0',
      widthTiles: gridWidth,
      heightTiles: gridHeight,
      tileSize,
      backgroundColor: level.backgroundColor || '#000000',
      layers,
      spawnPoints,
      gravity: level.gravity || 1000,
      // Per-level atmosphere (Scene FX v3 M3) — carried verbatim (opaque to shared).
      effectLayers: level.effectLayers,
      // Per-level scene metadata (scene-schema) — carried verbatim (opaque to shared).
      scene: level.scene,
      // Preserve metadata (lobbyTools, author, etc.) across conversion.
      metadata: level.metadata,
    };

    console.log(`[LevelFormatConverter] Reverse conversion complete: ${level.platforms.length} platforms → ${filledTiles} tiles`);
    console.log(`[LevelFormatConverter] Bounds: [${minX},${minY}] to [${maxX},${maxY}]`);

    return gridLevel;
  }

  /**
   * Reverse map: Platform type → TileType integer for rasterization
   */
  private static platformTypeToPlatformTileType(type: Platform['type']): number {
    switch (type) {
    case 'normal': return this.TILE_TYPE_FLOOR;
    case 'bouncy': return this.TILE_TYPE_BOUNCY;
    case 'moving': return this.TILE_TYPE_MOVING;
    case 'falling': return this.TILE_TYPE_FALLING;
    case 'teleporter': return this.TILE_TYPE_TELEPORTER;
    case 'ice': return this.TILE_TYPE_ICE;
    case 'rope': return this.TILE_TYPE_ROPE;
    default: return this.TILE_TYPE_FLOOR;
    }
  }

  /**
   * Check if a level object is in GridLevel format (needs conversion)
   * SPARSE: Checks for Map-based tiles storage
   */
  public static isGridLevel(level: any): boolean {
    if (!level || !level.layers || !Array.isArray(level.layers)) {
      return false;
    }
    // SPARSE: Check for Map-based tiles in first layer
    const firstLayer = level.layers[0];
    return !!(firstLayer?.tiles && firstLayer.tiles instanceof Map);
  }

  /**
   * Check if a level object is already in PlatformerLevel format
   */
  public static isPlatformerLevel(level: any): boolean {
    return !!(
      level &&
      level.platforms &&
      Array.isArray(level.platforms) &&
      level.width &&
      level.height
    );
  }
}
